import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { ErrorState, LoadingState } from '@/components/ui/state-views';
import { getProviderCalendarEvents } from '@/config/api-helpers';

type EventKind = 'appointment' | 'block' | 'external';
type Source = 'nourish' | 'healthie';

interface DisplayEvent {
  id: string;
  kind: EventKind;
  source: Source;
  inBoth: boolean;
  start: Date;
  end: Date;
  title: string;
}

interface LayoutEvent extends DisplayEvent {
  column: number;
  totalColumns: number;
}

const KIND_STYLES: Record<EventKind, string> = {
  appointment: 'bg-blue-100 border-blue-300 text-blue-900',
  block: 'bg-neutral-100 border-neutral-300 text-neutral-700',
  external: 'bg-amber-50 border-amber-300 text-amber-900',
};

const KIND_LABELS: Record<EventKind, string> = {
  appointment: 'Appointments',
  block: 'Blocks',
  external: 'External',
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const HOUR_START = 0;
const HOUR_END = 24;
const HOURS = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);
const PX_PER_HOUR = 60;
const TOTAL_HEIGHT = (HOUR_END - HOUR_START) * PX_PER_HOUR;

function getSunday(date: Date) {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, days: number) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function formatShortDate(date: Date) {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatTime(date: Date) {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function formatHour(hour: number) {
  if (hour === 0) return '12 AM';
  if (hour < 12) return `${hour} AM`;
  if (hour === 12) return '12 PM';
  return `${hour - 12} PM`;
}

function timeToPx(date: Date) {
  const hours = date.getHours() + date.getMinutes() / 60;
  return (hours - HOUR_START) * PX_PER_HOUR;
}

function layoutEvents(events: DisplayEvent[]): LayoutEvent[] {
  if (events.length === 0) return [];

  const sorted = [...events].sort((a, b) => a.start.getTime() - b.start.getTime());

  const columns: number[] = [];
  const assigned: { event: DisplayEvent; column: number }[] = [];

  for (const event of sorted) {
    let placed = false;
    for (let c = 0; c < columns.length; c++) {
      if (event.start.getTime() >= columns[c]) {
        columns[c] = event.end.getTime();
        assigned.push({ event, column: c });
        placed = true;
        break;
      }
    }
    if (!placed) {
      assigned.push({ event, column: columns.length });
      columns.push(event.end.getTime());
    }
  }

  const groups: number[][] = [];
  for (let i = 0; i < assigned.length; i++) {
    const a = assigned[i];
    let foundGroup = false;
    for (const group of groups) {
      const overlaps = group.some((j) => {
        const b = assigned[j];
        return a.event.start.getTime() < b.event.end.getTime() && a.event.end.getTime() > b.event.start.getTime();
      });
      if (overlaps) {
        group.push(i);
        foundGroup = true;
        break;
      }
    }
    if (!foundGroup) {
      groups.push([i]);
    }
  }

  const totalColumnsMap = new Map<number, number>();
  for (const group of groups) {
    const maxCol = Math.max(...group.map((i) => assigned[i].column)) + 1;
    for (const i of group) {
      totalColumnsMap.set(i, maxCol);
    }
  }

  return assigned.map(({ event, column }, i) => ({
    ...event,
    column,
    totalColumns: totalColumnsMap.get(i) ?? 1,
  }));
}

export function CalendarViewerTab({ providerId }: { providerId: number }) {
  const [weekStart, setWeekStart] = useState(() => getSunday(new Date()));
  const [visibleKinds, setVisibleKinds] = useState<Set<EventKind>>(new Set(['appointment', 'block', 'external']));

  const weekEnd = addDays(weekStart, 7);
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['provider-calendar-events', providerId, weekStart.toISOString(), weekEnd.toISOString()],
    queryFn: () =>
      getProviderCalendarEvents(providerId, { start: weekStart.toISOString(), end: weekEnd.toISOString() }),
  });

  const events = useMemo(() => {
    if (!data) return [];
    const result: DisplayEvent[] = [];

    if (visibleKinds.has('appointment')) {
      for (const a of data.appointments) {
        result.push({
          id: `n-appt-${a.id}`,
          kind: 'appointment',
          source: 'nourish',
          inBoth: a.inBoth,
          start: new Date(a.start),
          end: new Date(a.end),
          title: a.patientName ?? a.title ?? a.type ?? 'Appointment',
        });
      }
      for (const a of data.healthieAppointments) {
        if (a.inBoth) continue; // skip duplicates already shown via Nourish
        result.push({
          id: `h-appt-${a.id}`,
          kind: 'appointment',
          source: 'healthie',
          inBoth: false,
          start: new Date(a.start),
          end: new Date(a.end),
          title: a.title ?? 'Appointment',
        });
      }
    }

    if (visibleKinds.has('block')) {
      for (const b of data.blocks) {
        result.push({
          id: `n-block-${b.id}`,
          kind: 'block',
          source: 'nourish',
          inBoth: b.inBoth,
          start: new Date(b.start),
          end: new Date(b.end),
          title: b.title ?? 'Block',
        });
      }
      for (const b of data.healthieBlocks) {
        if (b.inBoth) continue;
        result.push({
          id: `h-block-${b.id}`,
          kind: 'block',
          source: 'healthie',
          inBoth: false,
          start: new Date(b.start),
          end: new Date(b.end),
          title: b.title ?? 'Block',
        });
      }
    }

    if (visibleKinds.has('external')) {
      for (const e of data.externalEvents) {
        result.push({
          id: `ext-${e.id}`,
          kind: 'external',
          source: 'nourish',
          inBoth: e.inBoth,
          start: new Date(e.start),
          end: new Date(e.end),
          title: e.title ?? 'External event',
        });
      }
      for (const e of data.healthieExternalEvents) {
        if (e.inBoth) continue; // skip duplicates already shown via Nourish
        result.push({
          id: `h-ext-${e.id}`,
          kind: 'external',
          source: 'healthie',
          inBoth: false,
          start: new Date(e.start),
          end: new Date(e.end),
          title: e.title ?? 'External event',
        });
      }
    }

    return result;
  }, [data, visibleKinds]);

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, [weekStart]);

  function toggleKind(kind: EventKind) {
    setVisibleKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) {
        next.delete(kind);
      } else {
        next.add(kind);
      }
      return next;
    });
  }

  function getEventsForDay(day: Date) {
    const dayStart = new Date(day);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(day);
    dayEnd.setHours(23, 59, 59, 999);
    return events.filter((e) => e.start < dayEnd && e.end > dayStart);
  }

  const scrollRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      node.scrollTop = 6 * PX_PER_HOUR;
    }
  }, []);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className='text-sm'>
      {/* Header: nav + filters */}
      <div className='flex items-center justify-between border-b border-neutral-100 px-5 py-3'>
        <div className='flex items-center gap-2'>
          <button
            type='button'
            className='rounded px-2 py-1 text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700'
            onClick={() => setWeekStart(addDays(weekStart, -7))}
          >
            &larr;
          </button>
          <span className='min-w-40 text-center font-medium text-neutral-900'>
            {formatShortDate(weekStart)} &ndash; {formatShortDate(addDays(weekStart, 6))}
          </span>
          <button
            type='button'
            className='rounded px-2 py-1 text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700'
            onClick={() => setWeekStart(addDays(weekStart, 7))}
          >
            &rarr;
          </button>
          <button
            type='button'
            className='ml-2 rounded px-2 py-1 text-xs text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700'
            onClick={() => setWeekStart(getSunday(new Date()))}
          >
            Today
          </button>
        </div>
        <div className='flex items-center gap-3'>
          {(['appointment', 'block', 'external'] as const).map((kind) => (
            <button
              key={kind}
              type='button'
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                visibleKinds.has(kind) ? KIND_STYLES[kind] : 'border-neutral-200 text-neutral-400'
              }`}
              onClick={() => toggleKind(kind)}
            >
              {KIND_LABELS[kind]}
            </button>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className='flex items-center gap-4 border-b border-neutral-100 px-5 py-2 text-xs text-neutral-500'>
        <span className='flex items-center gap-1'>
          <span className='inline-flex size-3.5 items-center justify-center rounded bg-neutral-700 text-[9px] font-bold text-white'>
            N
          </span>{' '}
          Nourish only
        </span>
        <span className='flex items-center gap-1'>
          <span className='inline-flex size-3.5 items-center justify-center rounded bg-neutral-700 text-[9px] font-bold text-white'>
            H
          </span>{' '}
          Healthie only
        </span>
        <span className='flex items-center gap-1'>
          <span className='inline-flex size-3.5 items-center justify-center rounded bg-neutral-700 text-[9px] font-bold text-white'>
            B
          </span>{' '}
          Both
        </span>
        <span className='flex items-center gap-1'>
          <span className='inline-block size-2.5 rounded-sm border border-dashed border-red-300 bg-red-50' /> Missing
          from other source
        </span>
      </div>

      {error ? (
        <ErrorState message={error?.message ?? ''} onRetry={() => refetch()} title='Calendar events are unavailable' />
      ) : !data && isLoading ? (
        <LoadingState message='Loading calendar' />
      ) : (
        <div className={isLoading ? 'opacity-50' : ''}>
          {/* Day headers — fixed */}
          <div className='grid grid-cols-[3.5rem_repeat(7,1fr)]'>
            <div className='border-b border-neutral-100' />
            {weekDays.map((day, i) => {
              const isToday = day.getTime() === today.getTime();
              return (
                <div
                  key={i}
                  className={`border-b border-l border-neutral-100 p-2 text-center ${isToday ? 'bg-blue-50' : ''}`}
                >
                  <div className='text-xs text-neutral-400'>{DAYS[day.getDay()]}</div>
                  <div className={`text-sm font-medium ${isToday ? 'text-blue-600' : 'text-neutral-700'}`}>
                    {day.getDate()}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Scrollable grid body */}
          <div ref={scrollRef} className='h-[600px] overflow-y-auto'>
            <div className='grid grid-cols-[3.5rem_repeat(7,1fr)]'>
              {/* Time labels column */}
              <div className='relative' style={{ height: TOTAL_HEIGHT }}>
                {HOURS.filter((h) => h > HOUR_START).map((hour) => (
                  <div
                    key={hour}
                    className='absolute right-0 pr-2 text-right text-xs text-neutral-400'
                    style={{ top: (hour - HOUR_START) * PX_PER_HOUR - 6 }}
                  >
                    {formatHour(hour)}
                  </div>
                ))}
              </div>

              {/* Day columns */}
              {weekDays.map((day, dayIdx) => {
                const dayEvents = getEventsForDay(day);
                const laid = layoutEvents(dayEvents);

                return (
                  <div key={dayIdx} className='relative border-l border-neutral-100' style={{ height: TOTAL_HEIGHT }}>
                    {/* Hour grid lines */}
                    {HOURS.map((hour) => (
                      <div
                        key={hour}
                        className='absolute inset-x-0 border-t border-neutral-100'
                        style={{ top: (hour - HOUR_START) * PX_PER_HOUR }}
                      />
                    ))}

                    {/* Events */}
                    {laid.map((e) => {
                      const top = Math.max(0, timeToPx(e.start));
                      const bottom = Math.min(TOTAL_HEIGHT, timeToPx(e.end));
                      const height = Math.max(12, bottom - top);
                      const durationMins = (e.end.getTime() - e.start.getTime()) / 60000;

                      const widthPct = 100 / e.totalColumns;
                      const leftPct = (e.column / e.totalColumns) * 100;

                      // Highlight events only in one source with a dashed red border
                      const mismatchStyle = !e.inBoth ? 'ring-1 ring-dashed ring-red-400' : '';

                      const badgeLetter = e.inBoth ? 'B' : e.source === 'nourish' ? 'N' : 'H';

                      return (
                        <div
                          key={e.id}
                          className={`absolute z-10 overflow-hidden rounded border px-1 py-0.5 text-xs leading-tight ${
                            KIND_STYLES[e.kind]
                          } ${mismatchStyle}`}
                          style={{
                            top,
                            height,
                            left: `calc(${leftPct}% + 2px)`,
                            width: `calc(${widthPct}% - 4px)`,
                          }}
                          title={`[${badgeLetter}] ${e.title}\n${formatTime(e.start)} – ${formatTime(e.end)}${
                            !e.inBoth ? '\n⚠ Only in ' + e.source : ''
                          }`}
                        >
                          <div className='flex items-center gap-1 truncate'>
                            <span className='inline-flex size-3.5 shrink-0 items-center justify-center rounded bg-neutral-700 text-[9px] font-bold leading-none text-white'>
                              {badgeLetter}
                            </span>
                            <span className='truncate font-medium'>{e.title}</span>
                          </div>
                          {durationMins >= 30 && (
                            <div className='truncate opacity-70'>
                              {formatTime(e.start)} – {formatTime(e.end)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
